import { useState, useRef, useEffect } from "react";
import { api } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { 
  MessageCircle, 
  X, 
  Send, 
  Bot,
  User,
  Sparkles
} from "lucide-react";

export default function SamChat({ isOpen, onToggle, showToggleButton = true }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm Sam, your AI placement assistant. I can help you find available rotations, recommend placements, and answer questions about the system. How can I help you today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post("/chat", {
        message: userMessage,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      toast.error("Failed to get response from Sam");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm sorry, I'm having trouble processing your request right now. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Toggle Button - only show if showToggleButton is true */}
      {showToggleButton && (
        <button
          onClick={onToggle}
          className={`fixed bottom-20 right-6 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 z-[9999] ${
            isOpen 
              ? "bg-slate-700 text-slate-300" 
              : "bg-cyan-400 text-black shadow-[0_0_20px_rgba(34,211,238,0.4)] animate-pulse-glow"
          }`}
          data-testid="sam-toggle-btn"
        >
          {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        </button>
      )}

      {/* Chat Panel */}
      <div
        className={`fixed top-16 right-6 w-96 h-[calc(100vh-100px)] bg-[#151A23] border border-slate-700 rounded-2xl shadow-2xl flex flex-col z-[9999] transition-all duration-300 ${
          isOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4 pointer-events-none"
        }`}
        data-testid="sam-chat-panel"
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-400/10 rounded-xl flex items-center justify-center">
              <Bot className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-100 flex items-center gap-2">
                Sam
                <Sparkles className="w-4 h-4 text-cyan-400" />
              </h3>
              <p className="text-xs text-slate-500">AI Placement Assistant</p>
            </div>
          </div>
          <button
            onClick={onToggle}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
            data-testid="sam-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  msg.role === "user" 
                    ? "bg-cyan-400/10" 
                    : "bg-slate-700"
                }`}>
                  {msg.role === "user" 
                    ? <User className="w-4 h-4 text-cyan-400" />
                    : <Bot className="w-4 h-4 text-slate-300" />
                  }
                </div>
                <div
                  className={`max-w-[80%] p-3 rounded-xl text-sm whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-cyan-400/10 text-slate-200"
                      : "bg-slate-800 text-slate-300"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-slate-300" />
                </div>
                <div className="bg-slate-800 p-3 rounded-xl">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Sam anything..."
              className="flex-1 bg-slate-800/50 border-slate-700 text-slate-200 placeholder:text-slate-500"
              disabled={loading}
              data-testid="sam-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="bg-cyan-400 text-black hover:bg-cyan-300"
              data-testid="sam-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
